import React, { useContext, useEffect, useState } from "react";
import Modal from "components/modal";
import { ProfilingAttributes } from "./profiling-attributes";
import { useValidateAttributePair } from "shared/hooks/use-validate-attribute-pair";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { useAttributeSubgroups } from "shared/hooks/use-attribute-subgroups";
import { AttributesContext, ProductsContext } from "contexts/attributes";
import AddAttributeSearch from "components/coding/components/add-attribute-search";
import { uniqBy } from "lodash";

export const AttributesModal = ({
    upc,
    productModifier,
    codingWipKey,
    isFinalized,
    isTaskCompleted,
    onValidateGroupAttributes,
    readOnlyEdit,
    itemInfo,
    hasMissingSizeAttributes,
    showMissingSizeAttributes,
}) => {
    const { products, refresh, bulkRefresh } = useContext(ProductsContext);
    const { refreshAttributes } = useContext(AttributesContext);
    const isBrandVerified = itemInfo?.context.workflowTypeId === 2;
    const {
        isSuccess,
        refetch: refetchSubgroups,
        data: attributeGroups,
        isLoading,
    } = useAttributeSubgroups(["IntakeProfilingSizeGroup", "IntakeProfilingIdentifyingGroup"], codingWipKey, isBrandVerified);
    const [showModal, setShowModal] = useState(false);
    const validatePair = useValidateAttributePair();
    const headerLabel = `${upc}${productModifier ? `-${productModifier}` : ""}`;

    useEffect(() => {
        if (refreshAttributes || bulkRefresh) {
            refetchSubgroups();
        }
    }, [refreshAttributes, bulkRefresh]);

    useEffect(() => {
        if (!showModal && !isFinalized) {
            refresh();
        }
    }, [showModal, isFinalized]);

    useEffect(() => {
        if (isSuccess && attributeGroups) {
            const hasMissingRequiredAttributes = hasEmptyRequiredAttributes(products[codingWipKey]);
            showMissingSizeAttributes(hasMissingRequiredAttributes && hasMissingRequiredAttributes.length > 0);
            onValidateGroupAttributes(uniqBy(attributeGroups.attributes, "key"));
        }
    }, [products, attributeGroups, isLoading, codingWipKey]);

    const hasEmptyRequiredAttributes = (product) => {
        return attributeGroups.attributes.reduce((arr, attr) => {
            const attributeValue = product[attr.uuid];
            const isAttributeRequired = attr.required && (attributeValue === "" || !attributeValue);

            if (isAttributeRequired) {
                arr.push(attr);
            }

            if (attr.pairedAttributes && !attr.readOnly) {
                const [pairedAttribute] = attr.pairedAttributes;

                if (
                    pairedAttribute &&
                    pairedAttribute.required &&
                    validatePair({ value: product[attr.uuid] }, { value: product[pairedAttribute.uuid] })
                ) {
                    arr.push(attr);
                }
            }

            return uniqBy(arr, "key");
        }, []);
    };

    return (
        <>
            <div className="edit-size-attr-button-container">
                <label>Size & Identifying Attributes:</label>
                <div className="edit-size-attr-button">
                    <button
                        className="button -edit-btn"
                        onClick={() => setShowModal(true)}
                        disabled={readOnlyEdit}
                    >
                        Edit
                    </button>
                    {hasMissingSizeAttributes && (
                        <p className="required-attributes">
                            <FontAwesomeIcon icon={faCircleExclamation} /> Required Attributes are missing
                        </p>
                    )}
                </div>
            </div>

            <Modal
                show={showModal}
                cssClass={"profiling-size-attribute-modal"}
            >
                <div className="row">
                    <div className="column -span2">
                        <button
                            className="button"
                            onClick={() => setShowModal(false)}
                        >
                            Back
                        </button>
                    </div>

                    <h2 className="size-attribute-heading column -span7">Item Attributes ({headerLabel})</h2>
                    <AddAttributeSearch
                        cssClass={"column -span3 m-t-10"}
                        wipKey={codingWipKey}
                        taskType={"scheduling.default"}
                        isSuccess={isSuccess}
                        taskKey={itemInfo && itemInfo.key}
                        disabled={isFinalized || isTaskCompleted}
                        isBrandVerified={isBrandVerified}
                    />
                </div>

                <ProfilingAttributes
                    isFinalized={isFinalized}
                    isCompleted={isTaskCompleted}
                    attributes={attributeGroups}
                    isLoading={isLoading}
                    reloadAttributes={refetchSubgroups}
                    isBrandVerified={isBrandVerified}
                />
            </Modal>
        </>
    );
};
